import { SEAT_IDENTITIES, type SeatIdentity } from './match'

/** What `playersInfo[uid]` carries, as far as identity is concerned. */
export interface SeatInfo {
  // `| undefined` spelled out because the repo runs `exactOptionalPropertyTypes`
  // and the parsed doc type carries it — omitting it makes this interface
  // incompatible with the very shape it exists to read.
  identity?: SeatIdentity | undefined
  createdBy?: string | undefined
  /** @deprecated pre-ladder flag; still on every match written before this. */
  guest?: boolean | undefined
}

/**
 * The identity of a seat, tolerating every doc written before the ladder existed.
 *
 * Legacy docs have no `identity` — only the old boolean `guest`. Inferring it
 * here, once, is what lets the rest of the app treat the ladder as if it had
 * always been there. A migration would have to touch every historical match to
 * gain nothing: these are finished games.
 */
export function seatIdentity(info: SeatInfo | undefined): SeatIdentity {
  if (info?.identity) return info.identity
  return info?.guest ? 'guest' : 'account'
}

/** Rung comparison. The ladder's ORDER is the rule; see SEAT_IDENTITIES. */
export function atLeast(identity: SeatIdentity, floor: SeatIdentity): boolean {
  return SEAT_IDENTITIES.indexOf(identity) >= SEAT_IDENTITIES.indexOf(floor)
}

/**
 * WHO ANSWERS for a no-show on this seat — the single most consequential
 * function in the model, because it decides whose public reputation moves.
 *
 * A seat at `guest` or above put itself there: it answers for itself. A
 * `placeholder` never consented to anything — somebody typed their name — so
 * charging them would be both unfair and useless, since nobody is watching that
 * uid. It belongs to whoever made the assertion.
 *
 * Returns null when a placeholder has no `createdBy`: that is a malformed seat,
 * and the caller must skip it rather than guess. Guessing here would mean
 * writing a no-show onto an arbitrary player.
 */
export function accountableFor(uid: string, info: SeatInfo | undefined): string | null {
  const identity = seatIdentity(info)
  if (atLeast(identity, 'guest')) return uid
  return info?.createdBy ?? null
}

/** The slice of `players_public` the vouching cap reads. */
export interface VouchingRecord {
  guestShows?: number | undefined
  guestNoShows?: number | undefined
  openPlaceholders?: number | undefined
}

// Mirrors functions/player_projection.py — VOUCH_BASE / VOUCH_NO_SHOW_COST /
// VOUCH_CEILING. Two copies because the rule must hold on a hand-rolled client
// too; the numbers are asserted equal in seat-identity.test.ts.
export const VOUCH_BASE = 3
export const VOUCH_NO_SHOW_COST = 2
export const VOUCH_CEILING = 6

/**
 * How many unclaimed placeholder seats this player has EARNED the right to
 * hold open at once (spec/02 § Seats & identity).
 *
 * Keyed on whether the people you vouch for TURN UP — deliberately never on
 * whether they installed the app. Gating a player behind our own acquisition
 * funnel would punish them for our failure, and the single behaviour this
 * model depends on is that organisers keep inviting. Claim rate is an
 * acquisition metric; it belongs in the funnel, not in a permission.
 *
 * Everyone starts at 3 — a whole court of companions, because the fixed
 * cuadrilla booking one court for its four regulars is the most common thing
 * in padel. Each companion who turns up earns another (ceiling 6); each
 * no-show costs two, so one flake leaves room to try again and two closes the
 * door until somebody turns up. Earnable back on purpose: a permanent penalty
 * is a permanent reason to stop using the feature.
 */
export function vouchingAllowance(record: VouchingRecord | null | undefined): number {
  const shows = Math.max(Math.trunc(Number(record?.guestShows ?? 0)) || 0, 0)
  const noShows = Math.max(Math.trunc(Number(record?.guestNoShows ?? 0)) || 0, 0)
  const earned = VOUCH_BASE + shows - VOUCH_NO_SHOW_COST * noShows
  return Math.max(0, Math.min(VOUCH_CEILING, earned))
}

/**
 * Has this player run out of room to seat companions?
 *
 * `openPlaceholders` is CF-maintained and lags the write that adds a seat, so
 * this is a ceiling and not an exact count. Clamped at 0 because a counter that
 * drifted negative would otherwise hand out unlimited seats — the permissive
 * direction is the one worth defending against.
 */
export function vouchingExhausted(record: VouchingRecord | null | undefined): boolean {
  const open = Math.max(Math.trunc(Number(record?.openPlaceholders ?? 0)) || 0, 0)
  return open >= vouchingAllowance(record)
}

/**
 * May `actor` add a placeholder to this match?
 *
 * Any registered player ALREADY IN the match — not the organizer only. Padel is
 * played in pairs, so "voy con mi compañero" is the most common case there is,
 * and routing it through the organizer makes them an administrator for
 * everyone. Organizer-only is a proxy for accountability; this model has the
 * real thing, which makes the proxy redundant friction.
 *
 * A placeholder cannot add anyone: it has no session and never acts.
 */
export function canAddPlaceholder(
  actor: string,
  match: { players: string[]; playersInfo: Record<string, SeatInfo> },
): boolean {
  if (!match.players.includes(actor)) return false
  return atLeast(seatIdentity(match.playersInfo[actor]), 'guest')
}

/**
 * May `actor` remove the seat held by `target`?
 *
 * Only the creator of a placeholder may remove it. The organizer keeps the
 * existing "quitar jugador" power, so an unclaimed seat can always be cleared —
 * but one player may never evict another's guest, which would be a way to free
 * seats you do not own.
 */
export function canRemoveSeat(
  actor: string,
  target: string,
  match: { organizer: string; playersInfo: Record<string, SeatInfo> },
): boolean {
  if (actor === target) return true
  if (actor === match.organizer) return true
  const info = match.playersInfo[target]
  return seatIdentity(info) === 'placeholder' && info?.createdBy === actor
}
